/*
 * Product page behaviour.
 *
 * <product-section> is a "custom element": the browser runs connectedCallback()
 * automatically when it appears on the page. From there we listen for:
 *   - colour / size clicks  -> find the matching variant, update price, button, photo, URL
 *   - thumbnail & arrow clicks -> move the gallery
 *   - quantity − / + clicks
 *   - add to cart -> add in the background, update the header cart count
 */
class ProductSection extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.currency = this.dataset.currency;
    this.track = this.querySelector('[data-gallery-track]');
    this.form = this.querySelector('[data-product-form]');

    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-option-index]')) this.onOptionChange();
    });

    this.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-thumb-index]');
      if (thumb) this.goToSlide(Number(thumb.dataset.thumbIndex));

      if (event.target.closest('[data-gallery-prev]')) this.goToSlide(this.currentSlide() - 1);
      if (event.target.closest('[data-gallery-next]')) this.goToSlide(this.currentSlide() + 1);

      const qtyButton = event.target.closest('[data-qty-change]');
      if (qtyButton) this.changeQuantity(Number(qtyButton.dataset.qtyChange));
    });

    // Keep the active thumbnail in sync when swiping the gallery on mobile
    this.track?.addEventListener('scroll', () => {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.highlightThumb(this.currentSlide()), 80);
    });

    this.form?.addEventListener('submit', (event) => this.onSubmit(event));

    // "Notify me when available" (sold-out variants) -> Klaviyo
    this.notifyLabel = this.querySelector('[data-notify-label]')?.dataset.notifyLabel || '';
    this.notifyDialog = this.querySelector('[data-notify-dialog]');
    this.notifyDialog?.addEventListener('click', (event) => {
      if (event.target.closest('[data-notify-close]') || event.target === this.notifyDialog) this.notifyDialog.close();
    });
    this.notifyDialog?.querySelector('[data-notify-form]').addEventListener('submit', (event) => {
      event.preventDefault();
      this.submitNotify(event.target);
    });

    this.setupSizeGuide();
    this.setupStickyBuy();

    this.updateAvailability();
    this.showSelectedVariantMedia();
    this.updateShipEstimate(this.variants.find((v) => v.id === Number(this.querySelector('[data-variant-id]')?.value)));
  }

  // Opened via a colour link (?variant=...)? Start the gallery on that colour's photo.
  showSelectedVariantMedia() {
    const id = Number(this.querySelector('[data-variant-id]')?.value);
    const variant = this.variants.find((v) => v.id === id);
    if (!variant?.featured_media || !this.track) return;
    const slide = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
    const index = [...this.track.children].indexOf(slide);
    if (index > 0) {
      this.track.scrollLeft = index * this.track.clientWidth; // jump instantly, no animation
      this.highlightThumb(index);
    }
  }

  /* ---------- Colour & size ---------- */

  // The value currently ticked for each option, e.g. ["powder pink", "M"]
  selectedOptions() {
    return [...this.querySelectorAll('fieldset.option')].map(
      (fieldset) => fieldset.querySelector('input:checked')?.value
    );
  }

  onOptionChange() {
    const selected = this.selectedOptions();
    const variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i]));

    this.updateAvailability();
    this.updateColourName(selected);

    if (!variant) {
      this.setButton(false, 'unavailable');
      this.updateDispatchNote(null);
      this.updateShipEstimate(null);
      return;
    }

    this.querySelector('[data-variant-id]').value = variant.id;
    this.updatePrice(variant);
    this.updateDispatchNote(variant);
    this.updateShipEstimate(variant);
    this.currentVariant = variant;
    if (variant.available) this.setButton(true, 'add to cart');
    else if (this.notifyLabel) this.setButton(true, this.notifyLabel, true);
    else this.setButton(false, 'sold out');

    // Jump the gallery to this variant's photo (e.g. the butter hoodie)
    if (variant.featured_media) {
      const slide = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
      if (slide) this.goToSlide([...this.track.children].indexOf(slide));
    }

    // Update the address bar (?variant=123) so the link can be shared/refreshed
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  // Cross out option values that can't be bought:
  //  - colours: only when EVERY size in that colour is sold out
  //  - sizes: when that size is sold out in the currently chosen colour
  updateAvailability() {
    const selected = this.selectedOptions();

    this.querySelectorAll('input[data-option-index]').forEach((input) => {
      const index = Number(input.dataset.optionIndex);
      const isColour = input.closest('fieldset').classList.contains('option--colour');

      const available = this.variants.some(
        (v) =>
          v.available &&
          v.options[index] === input.value &&
          (isColour || v.options.every((value, i) => i === index || value === selected[i]))
      );
      input.nextElementSibling.classList.toggle('is-unavailable', !available);
    });
  }

  updateColourName(selected) {
    const label = this.querySelector('[data-colour-name]');
    if (label) label.textContent = selected[Number(label.dataset.optionIndex)];
  }

  updatePrice(variant) {
    const priceEl = this.querySelector('[data-price]');
    if (!priceEl) return;

    const current = this.formatMoney(variant.price);
    if (variant.compare_at_price > variant.price) {
      priceEl.innerHTML = `<s>${this.formatMoney(variant.compare_at_price)}</s> <span class="product__price--sale">${current}</span>`;
    } else {
      priceEl.innerHTML = current;
    }
  }

  // Dispatch note block: show this variant's note (pre-order timing etc.), or hide it if there isn't one
  updateDispatchNote(variant) {
    const block = this.querySelector('[data-dispatch-note]');
    if (!block) return;
    this.dispatchNotes ??= JSON.parse(block.querySelector('[data-dispatch-notes]').textContent);
    const note = variant ? this.dispatchNotes[variant.id] : '';
    block.querySelector('[data-dispatch-text]').textContent = note || '';
    block.hidden = !note;
  }

  // Estimated dispatch & delivery block: dates for in-stock variants, hidden for sold-out / "hide" variants
  updateShipEstimate(variant) {
    const block = this.querySelector('[data-ship-estimate]');
    if (!block) return;
    this.shipEstimateHide ??= JSON.parse(block.querySelector('[data-ship-estimate-variants]').textContent);

    if (!variant || !variant.available || this.shipEstimateHide[variant.id]) {
      block.hidden = true;
      return;
    }

    const dispatch = addBusinessDays(new Date(), Number(block.dataset.dispatchDays));
    const delivery = addBusinessDays(dispatch, Number(block.dataset.deliveryDays));
    block.querySelector('[data-dispatch-date]').textContent = formatShipDate(dispatch);
    block.querySelector('[data-delivery-date]').textContent = formatShipDate(delivery);
    block.hidden = false;
  }

  // 14400 cents -> "144 AUD"  (matches snippets/price.liquid)
  formatMoney(cents) {
    const amount = cents / 100;
    const text = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
    return `<span class="money">${text} ${this.currency}</span>`;
  }

  setButton(enabled, label, notify = false) {
    const button = this.querySelector('[data-add-button]');
    if (!button) return;
    button.disabled = !enabled;
    button.textContent = label;
    button.classList.toggle('is-notify', notify); // clicking opens the "notify me" form instead
  }

  /* ---------- Size guide pop-up ---------- */

  // Opens the size calculator page inside a pop-up. The page is only fetched the first time it's opened.
  setupSizeGuide() {
    const dialog = this.querySelector('[data-size-guide]');
    if (!dialog) return;

    // The "size guide" button, or any link on the product page to the size calculator page
    const pagePath = new URL(dialog.dataset.url, location.origin).pathname;
    this.addEventListener('click', (event) => {
      const link = event.target.closest('[data-size-guide-open], a[href]');
      if (!link || dialog.contains(link)) return;
      if (link.matches('a') && !/size-calculator/.test(link.pathname) && link.pathname !== pagePath) return;
      event.preventDefault();
      dialog.showModal();
      dialog.focus(); // focus the pop-up itself, not the ✕ (no ring around it)
      document.documentElement.classList.add('has-dialog-open');
      if (!dialog.dataset.loaded) this.loadSizeGuide(dialog);
    });
    dialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-size-guide-close]') || event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => document.documentElement.classList.remove('has-dialog-open'));

    // Fetch it quietly in the background once the page has settled, so it's ready before it's tapped
    const preload = () => { if (!dialog.dataset.loaded) this.loadSizeGuide(dialog); };
    if ('requestIdleCallback' in window) requestIdleCallback(preload, { timeout: 4000 });
    else setTimeout(preload, 2000);
  }

  async loadSizeGuide(dialog) {
    const body = dialog.querySelector('[data-size-guide-body]');
    dialog.dataset.loaded = 'true';
    try {
      const response = await fetch(dialog.dataset.url);
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const calculator = page.querySelector('size-calculator');
      if (!calculator) throw new Error('no size calculator on that page');

      // The page's stylesheet + script for the calculator, if this page doesn't have them yet
      page.querySelectorAll('link[rel="stylesheet"][href*="pages.css"]').forEach((link) => {
        if (!document.querySelector('link[href*="pages.css"]')) document.head.append(link.cloneNode());
      });
      if (!customElements.get('size-calculator')) {
        const src = page.querySelector('script[src*="size-calculator.js"]')?.src;
        if (src) document.head.append(Object.assign(document.createElement('script'), { src }));
      }

      // Take the whole section (it carries its own text-size styles), not just the calculator
      body.replaceChildren(document.importNode(calculator.closest('.shopify-section') || calculator, true));
    } catch (error) {
      delete dialog.dataset.loaded;
      body.innerHTML = `<p class="size-guide__fallback"><a href="${dialog.dataset.url}">open the size guide</a></p>`;
    }
  }

  /* ---------- Sticky add to cart (phones) ---------- */

  setupStickyBuy() {
    const bar = this.querySelector('[data-sticky-buy]');
    const mainButton = this.querySelector('[data-add-button]');
    if (!bar || !mainButton) return;
    const stickyButton = bar.querySelector('[data-sticky-button]');

    // Copy the main button's label/state (add to cart, sold out, notify me, adding...)
    const syncButton = () => {
      stickyButton.textContent = mainButton.textContent.trim();
      stickyButton.disabled = mainButton.disabled;
    };
    syncButton();
    new MutationObserver(syncButton).observe(mainButton, { childList: true, characterData: true, subtree: true, attributes: true });

    // Copy the price and "colour / size" whenever the price changes (it changes with every variant pick)
    const syncDetails = () => {
      const price = this.querySelector('[data-price]');
      if (price) bar.querySelector('[data-sticky-price]').innerHTML = price.innerHTML;
      const id = Number(this.querySelector('[data-variant-id]')?.value);
      const variant = this.variants.find((v) => v.id === id);
      if (variant && this.variants.length > 1) bar.querySelector('[data-sticky-variant]').textContent = variant.title.toLowerCase();
    };
    this.addEventListener('change', () => requestAnimationFrame(syncDetails));

    // Show the bar only once the main button has scrolled up out of view
    new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      stickyButton.tabIndex = show ? 0 : -1;
    }).observe(mainButton);
  }

  /* ---------- Gallery ---------- */

  currentSlide() {
    if (!this.track) return 0;
    return Math.round(this.track.scrollLeft / this.track.clientWidth);
  }

  goToSlide(index) {
    if (!this.track) return;
    const count = this.track.children.length;
    const wrapped = (index + count) % count; // past the last photo loops back to the first
    this.track.scrollTo({ left: wrapped * this.track.clientWidth, behavior: 'smooth' });
    this.highlightThumb(wrapped);
  }

  highlightThumb(index) {
    this.querySelectorAll('[data-thumb-index]').forEach((thumb) => {
      const active = Number(thumb.dataset.thumbIndex) === index;
      thumb.classList.toggle('is-active', active);
      if (active) this.scrollThumbIntoView(thumb);
    });
  }

  // Move the thumbnail strip so the active one is visible: up/down when the strip is a column
  // (desktop), sideways when it's a row. Only the strip moves - the page itself never scrolls.
  scrollThumbIntoView(thumb) {
    const strip = thumb.closest('.gallery__thumbs');
    if (!strip) return;
    const item = thumb.parentElement; // the <li>
    const vertical = strip.scrollHeight > strip.clientHeight + 1;
    const start = vertical ? item.offsetTop - strip.offsetTop : item.offsetLeft - strip.offsetLeft;
    const size = vertical ? item.offsetHeight : item.offsetWidth;
    const scrolled = vertical ? strip.scrollTop : strip.scrollLeft;
    const visible = vertical ? strip.clientHeight : strip.clientWidth;
    let target = null;
    if (start < scrolled) target = start;
    else if (start + size > scrolled + visible) target = start + size - visible;
    if (target !== null) strip.scrollTo({ [vertical ? 'top' : 'left']: target, behavior: 'smooth' });
  }

  /* ---------- Notify me when available (Klaviyo) ---------- */

  openNotify() {
    const dialog = this.notifyDialog;
    if (!dialog) return;
    const id = Number(this.querySelector('[data-variant-id]').value);
    this.notifyVariant = this.variants.find((v) => v.id === id);
    const title = this.querySelector('.product__title')?.textContent.trim() || '';
    dialog.querySelector('[data-notify-item]').textContent =
      this.notifyVariant ? `${title} - ${this.notifyVariant.title}`.toLowerCase() : title;
    dialog.querySelector('[data-notify-form]').hidden = false;
    dialog.querySelector('[data-notify-success]').hidden = true;
    dialog.querySelector('[data-notify-message]').textContent = '';
    dialog.showModal();
    dialog.querySelector('input[type="email"]').focus();
  }

  async submitNotify(form) {
    const dialog = this.notifyDialog;
    const message = dialog.querySelector('[data-notify-message]');
    const email = form.email.value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      message.textContent = 'please enter a valid email.';
      return;
    }

    // Phone number (required when the field is shown) -> international format (+61412345678) for Klaviyo
    const phoneInput = form.querySelector('input[name="phone"]');
    let phone = '';
    if (phoneInput) {
      if (!phoneInput.value.trim()) {
        message.textContent = 'please enter your phone number.';
        return;
      }
      phone = toInternationalPhone(phoneInput.value, phoneInput.dataset.countryCode || '61');
      if (!phone) {
        message.textContent = 'please check your phone number.';
        return;
      }
    }

    const key = dialog.dataset.klaviyoKey;
    const listId = dialog.dataset.klaviyoList;
    const variantId = this.notifyVariant?.id;
    const headers = { 'Content-Type': 'application/vnd.api+json', revision: '2024-10-15' };
    const button = form.querySelector('button[type="submit"]');
    button.disabled = true;
    message.textContent = '';

    try {
      const requests = [];

      // Back in Stock request for this exact colour/size (Klaviyo's Back in Stock flow emails them on restock)
      if (variantId) {
        requests.push(
          fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${key}`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              data: {
                type: 'back-in-stock-subscription',
                attributes: {
                  channels: ['EMAIL'],
                  profile: { data: { type: 'profile', attributes: { email } } },
                },
                relationships: {
                  variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
                },
              },
            }),
          })
        );
      }

      // Also add them to the chosen list (e.g. "re-stock notifications")
      if (listId) {
        requests.push(
          fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${key}`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  custom_source: 'notify me when available',
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: {
                        email,
                        ...(phone && { phone_number: phone }),
                        properties: {
                          'restock product': this.querySelector('.product__title')?.textContent.trim(),
                          'restock variant': this.notifyVariant?.title,
                          'restock variant id': variantId,
                        },
                      },
                    },
                  },
                },
                relationships: { list: { data: { type: 'list', id: listId } } },
              },
            }),
          })
        );
      }

      // Text-message restock alert too, if they gave a number. Sent separately so that if SMS isn't
      // set up in Klaviyo, the email sign-up above still succeeds.
      if (phone && variantId) {
        fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${key}`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['SMS'],
                profile: { data: { type: 'profile', attributes: { email, phone_number: phone } } },
              },
              relationships: {
                variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
              },
            },
          }),
        }).catch(() => {});
      }

      const responses = await Promise.all(requests);
      if (responses.some((r) => !r.ok)) throw new Error('klaviyo');

      form.hidden = true;
      dialog.querySelector('[data-notify-success]').hidden = false;
      form.reset();
    } catch {
      message.textContent = 'something went wrong - please try again.';
    } finally {
      button.disabled = false;
    }
  }

  /* ---------- Quantity ---------- */

  changeQuantity(step) {
    const input = this.querySelector('input[name="quantity"]');
    input.value = Math.max(1, (Number(input.value) || 1) + step);
  }

  /* ---------- Add to cart ---------- */

  async onSubmit(event) {
    event.preventDefault();
    const button = this.querySelector('[data-add-button]');
    if (button.classList.contains('is-notify')) {
      this.openNotify();
      return;
    }
    const error = this.querySelector('[data-form-error]');
    error.hidden = true;
    button.disabled = true;

    try {
      // FormData picks up the variant id AND the quantity box (linked via form="...")
      const body = new FormData(this.form);

      // If the site uses the cart drawer, ask Shopify to send back a freshly rendered drawer too
      const hasDrawer = Boolean(document.querySelector('cart-drawer'));
      if (hasDrawer) body.append('sections', 'cart-drawer');

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'could not add to cart');

      document.dispatchEvent(new CustomEvent('cart:updated'));

      // Starts the "cart reserved" timer (theme.js); the drawer also swaps in its new contents and slides open
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { html: result.sections?.['cart-drawer'] } }));

      if (hasDrawer) {
        this.setButton(true, 'add to cart');
      } else {
        button.textContent = 'added';
        setTimeout(() => this.setButton(true, 'add to cart'), 2000);
      }
    } catch (err) {
      error.textContent = err.message.toLowerCase();
      error.hidden = false;
      button.disabled = false;
    }
  }
}

// Move forward N business days, skipping Saturdays and Sundays (e.g. Friday + 1 = Monday)
function addBusinessDays(date, days) {
  const d = new Date(date);
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay(); // 0 = Sunday, 6 = Saturday
    if (day !== 0 && day !== 6) added++;
  }
  return d;
}

// "Wednesday 30 September" (the site shows it lowercase)
function formatShipDate(date) {
  return date.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' }).replace(',', '');
}

// "0412 345 678" -> "+61412345678" (country code from the block setting). Numbers already
// starting with + are kept as they are. Returns '' if it doesn't look like a real number.
function toInternationalPhone(raw, countryCode) {
  const trimmed = raw.trim();
  const digits = trimmed.replace(/\D/g, '');
  let full;
  if (trimmed.startsWith('+')) full = `+${digits}`;
  else if (digits.startsWith('00')) full = `+${digits.slice(2)}`;
  else if (digits.startsWith(countryCode)) full = `+${digits}`;
  else full = `+${countryCode}${digits.replace(/^0/, '')}`;
  return /^\+\d{8,15}$/.test(full) ? full : '';
}

customElements.define('product-section', ProductSection);
// (The header cart count is refreshed by assets/theme.js, which listens for "cart:updated".)
